import { IQuat } from "@base/math/Quat";
import { IVec3, Vec3 } from "@base/math/Vec3";
import { eqc } from "@base/util/Comma";
import { ImmHelper } from "@base/util/Immutable";
import { SST } from "@biz/definitions/SST";
import { StandardTemplate } from "@biz/definitions/StandardTemplate";
import {
    Auto, AxleColor, CornerType, IDistIn, IDistOut, IPoleData, IPoleDataRopes, IStandardTemplate, ISunSailData, ISunSailRopes, MaterialType, PoleId, PoleIndex, PoleMountType, PoleType, PylonType, SailBordersType, SailMount, StandardTemplateId,
    StandardTemplateShape, SunSailType, WaterDirection, WinchHeightChangePriority
} from "@biz/definitions/SunSailDefinitions";
import { EditorRawState } from "@biz/draftEditor/EditorDefinitions";
import { LogicS } from "@biz/items/sunSail/logic/LogicS";
import { PoleHelper } from "@biz/items/sunSail/logic/PoleHelper";
import { PoleLogic } from "@biz/items/sunSail/logic/PoleLogic";
import { Poles } from "@biz/items/sunSail/logic/Poles";
import { SunSailLogic } from "@biz/items/sunSail/logic/SunSailLogic";
import { getSail2d3dReducers, ISetPoleDataOptions, updateStandardPositions } from "@biz/items/sunSail/SunSailReducers.2d3d";
import { ArticleLogic } from "@biz/logic/ArticleLogic";
import { getClothColorReducers } from "@biz/reducers/ClothColorReducer";
import { getSlotReducer } from "@biz/reducers/CommonReducers";
import { getEdgeReducers } from "@biz/reducers/EdgeReducer";
import { getItemLayerReducers } from "@biz/reducers/ItemLayerReducer";
import { getProduct3dReducers } from "@biz/reducers/Product3dReducer";

export const PoleReducers = {
  setPosition(old: IPoleData, position: IVec3): IPoleData {
    if (!old)
      throw new Error("PoleReducers.setPosition: PoleData is null!");
    if (Vec3.equals(old.position, position))
      return old;
    return {
      ...old,
      position: Vec3.from(position.x, position.y, position.z)
    }
  },
  setArticleSlot<T extends keyof IPoleData["articleSlots"]>(old: IPoleData, name: T, articleId: IPoleData["articleSlots"][T]): IPoleData {
    return ImmHelper.setProperty(old, "articleSlots", name, articleId);
  },
  setCorner(old: IPoleData, cornerType: CornerType | undefined): IPoleData {
    return ImmHelper.setProperty(old, "cornerType", cornerType);
  },
  setLength(old: IPoleData, length: number): IPoleData {
    if (old.length === length)
      return old;
    return {
      ...old,
      length
    }
  },
  setAngle(old: IPoleData, angle: number): IPoleData {
    if (old.angle === angle)
      return old;
    return {
      ...old,
      angle
    }
  },
  setPoleType(old: IPoleData, poleType: PoleType): IPoleData {
    if (old.poleType === poleType)
      return old;
    return {
      ...old,
      poleType
    }
  },
  setPylonType(old: IPoleData, pylonType: PylonType | undefined): IPoleData {
    if (old.pylonType === pylonType)
      return old;
    return {
      ...old,
      pylonType
    }
  },
  setCoilUpHeight(old: IPoleData, height: Auto<number>): IPoleData {
    if (old.coilUpHeight === height)
      return old;

    return {
      ...old,
      coilUpHeight: height
    }
  },
  setHeightChangeCoilUpHeight(old: IPoleData, height: Auto<number>): IPoleData {
    if (old.heightChangeCoilUpHeight === height)
      return old;

    return {
      ...old,
      heightChangeCoilUpHeight: height
    }
  },
  setDist(old: IPoleData, dist: IDistOut | IDistIn): IPoleData {
    return {
      ...old,
      dist
    }
  },
  setDistIn(old: IPoleData, distInVal: Auto<number>, clothIndex: number): IPoleData {
    let newVal: Auto<number | number[]>;
    if (Array.isArray(old.dist?.val) && distInVal !== "auto")
      newVal = ImmHelper.setArrayItem(old.dist.val, clothIndex || 0, distInVal);
    else
      newVal = distInVal;

    return {
      ...old,
      dist: {
        val: newVal,
        type: "in"
      }
    }
  },
  setDistInSplit(old: IPoleData, flag: boolean): IPoleData {
    if (old.dist.val === "auto") // no split for auto mode
      return old;
    const v = PoleHelper.getArrayVal(old.dist.val);
    return {
      ...old,
      dist: {
        val: flag ? [v, v] : v,
        type: "in"
      }
    }
  },
  setDistOut(old: IPoleData, distOut: Auto<number>): IPoleData {
    return {
      ...old,
      dist: {
        val: distOut,
        type: "out"
      }
    }
  },
  setMountToAxle(old: IPoleData, mountToAxle: number | undefined): IPoleData {
    return {
      ...old,
      mountToAxle
    }
  },
  setDistType(old: IPoleData, flag: "in" | "out", val: Auto<number>): IPoleData {
    return {
      ...old,
      dist: {
        ...old.dist,
        type: flag,
        val
      }
    }
  },
  setLengthB(old: IPoleData, lengthB: number): IPoleData {
    return {
      ...old,
      lengthB: {
        enabled: true,
        ...old.lengthB,
        val: lengthB
      }
    }
  },
  setLengthBEnabled(old: IPoleData, flag: boolean): IPoleData {
    return {
      ...old,
      lengthB: {
        val: 0.5,
        ...old.lengthB,
        enabled: flag,
      }
    }
  },
  setTopMountY(old: IPoleData, topMountY: Auto<number>): IPoleData {
    return {
      ...old,
      topMountY
    }
  },
  setSpecialConsoleNotes(old: IPoleData, notes: EditorRawState): IPoleData {
    return {
      ...old,
      specialConsole: {
        ...old.specialConsole,
        notes: {
          type: "raw",
          value: notes
        }
      }
    }
  },
  addSpecialConsoleFile(old: IPoleData, file: string): IPoleData {
    return {
      ...old,
      specialConsole: {
        ...old.specialConsole,
        files: [
          ...(old.specialConsole?.files || []),
          file
        ]
      }
    }
  },
  removeSpecialConsoleFile(old: IPoleData, fileId: string): IPoleData {
    return {
      ...old,
      specialConsole: {
        ...old.specialConsole,
        files: (old.specialConsole?.files || []).filter((f) => f !== fileId)
      }
    }
  },
  setRopeArticleId(old: IPoleData, name: keyof IPoleDataRopes, articleId: string | undefined): IPoleData {
    if (!old[name])
      old = ImmHelper.setProperty(old, name, {})

    return ImmHelper.setPropertyNonNullable(old, name, "articleId", articleId)
  },
  setRopeLength(old: IPoleData, name: keyof IPoleDataRopes, length: number | undefined): IPoleData {
    if (!old[name])
      old = ImmHelper.setProperty(old, name, {})

    return ImmHelper.setPropertyNonNullable(old, name, "length", length)
  },
  setWinchHeightChangePriority(old: IPoleData, priority: WinchHeightChangePriority) {
    return ImmHelper.setProperty(old, "winchHeightChangePriority", priority)
  }
}

export const SunSailReducers = {
  ...getItemLayerReducers<ISunSailData>(),
  ...getClothColorReducers<ISunSailData>(),
  ...getProduct3dReducers<ISunSailData>(),
  ...getEdgeReducers<ISunSailData>(),
  ...getSail2d3dReducers<ISunSailData>(),
  setProductSlot: getSlotReducer<ISunSailData>(),
  setPolePositionsDirect(old: ISunSailData, cornerPositions: IVec3[], centerPositions?: IVec3[]): ISunSailData {
    old = { ...old, poles: old.poles.map((p, index) => ({ ...p, position: cornerPositions[index] })) }
    if (old.centerPoles && centerPositions) {
      old = { ...old, centerPoles: old.centerPoles.map((p, index) => ({ ...p, position: centerPositions[index] })) }
    }
    return old;
  },
  setPoleHalfVecs(old: ISunSailData, halfVecs: IVec3[]): ISunSailData {
    old = { ...old, poles: old.poles.map((p, index) => ({ ...p, halfVec: halfVecs[index] })) }
    return old;
  },
  setClothCount(old: ISunSailData, clothCount: number | undefined): ISunSailData {
    return ImmHelper.setProperty(old, "clothCount", clothCount);
  },
  setSailPosition(old: ISunSailData, pos: IVec3): ISunSailData {
    const center = SunSailLogic.calcSunSailLogicalCenter(old);
    const delta = Vec3.sub(pos, center);
    const positions = old.poles.map((p) => Vec3.add(p.position, delta))
    const centerPositions = old.centerPoles ? old.centerPoles.map((p) => Vec3.add(p.position, delta)) : undefined;
    return SunSailReducers.setPolePositionsDirect(old, positions, centerPositions);
  },
  setSailBordersType(old: ISunSailData, borderType: SailBordersType): ISunSailData {
    return ImmHelper.setProperty(old, "sailBordersType", borderType);
  },
  setSailPositionByPole(old: ISunSailData, poleIndex: number, pos: IVec3): ISunSailData {
    const delta = Vec3.sub(pos, Poles.getPole(old, poleIndex).position);
    const positions = old.poles.map((p) => Vec3.add(p.position, delta))
    const centerPositions = old.centerPoles ? old.centerPoles.map((p) => Vec3.add(p.position, delta)) : undefined;
    if (old?.specific?.type === "standard" && old.specific.center)
      old = SunSailReducers.setStandardCenter(old, Vec3.add(old.specific?.center, delta));
    return SunSailReducers.setPolePositionsDirect(old, positions, centerPositions);
  },
  setSailRotation(old: ISunSailData, rotation: IQuat): ISunSailData {
    const center = SunSailLogic.calcSunSailLogicalCenter(old);
    const positions = old.poles.map((p) => Vec3.add(center, Vec3.applyQuat(Vec3.sub(p.position, center), rotation)))
    const centerPositions = old.centerPoles ? old.centerPoles.map((p) => Vec3.add(center, Vec3.applyQuat(Vec3.sub(p.position, center), rotation))) : undefined;
    return SunSailReducers.setPolePositionsDirect(old, positions, centerPositions);
  },
  setTwistingSet(old: ISunSailData, twistingSet: boolean): ISunSailData {
    return ImmHelper.setProperty(old, "twistingSet", twistingSet);
  },
  setAlignDistOut(old: ISunSailData, alignDistOut: boolean): ISunSailData {
    return ImmHelper.setProperty(old, "alignDistOut", alignDistOut);
  },
  setCoilUpDeflectionPosition(old: ISunSailData, coilUpDeflectionPosition: PoleIndex | undefined): ISunSailData {
    return ImmHelper.setProperty(old, "coilUpDeflectionPosition", coilUpDeflectionPosition);
  },
  setMotorPosition(old: ISunSailData, motorPosition: PoleIndex | undefined): ISunSailData {
    return ImmHelper.setProperty(old, "motorPosition", motorPosition);
  },
  setPolyPositioning(old: ISunSailData, flag: boolean, groupIndex: number): ISunSailData {
    let ssd: ISunSailData = {
      ...old,
      polyPositioning: ImmHelper.setArrayItem(old.polyPositioning, groupIndex, flag)
    }
    for (let i = 0; i < Poles.getCount(old); i++) {
      if (!PoleLogic.isAxle(old.sunSailType, i))
        ssd = SunSailLogic.fixPoleData(ssd, i);
    }
    return SunSailReducers.fixPosition(ssd, groupIndex === 0 ? PoleIndex.C : PoleIndex.E);

  },
  setPolePositionDefault(old: ISunSailData, poleIndex: number, newPos: IVec3): ISunSailData {
    const { sunSailType } = old;
    if (PoleLogic.isAxle(sunSailType, poleIndex)) {
      return SunSailLogic.applyAxleSize(old, poleIndex, newPos);
    }
    return SunSailReducers.setPolePosition(old, poleIndex, newPos, { direct: true });
  },

  applyDistOut(old: ISunSailData, poleIndex: PoleIndex, newDistOut: number): ISunSailData {
    let newSunSail: ISunSailData = old;
    const pole = old.poles[poleIndex];
    if (pole.dist.type === "in")
      newSunSail = SunSailReducers.setDistIn(old, poleIndex, SunSailLogic.calcDistanceInFromOut(newDistOut, SunSailLogic.calcTotalMaxDistInOut(old, poleIndex)), 0)
    else
      newSunSail = SunSailReducers.setDistOut(old, poleIndex, newDistOut)
    return newSunSail;
  },

  equalizeDistOut(old: ISunSailData, primaryPole: PoleIndex | undefined): ISunSailData {
    if (SST.is(old.sunSailType, SST.AMCXOne) && Poles.getCount(old) === 4) {
      if (primaryPole !== undefined && PoleLogic.isAxle(old.sunSailType, primaryPole))
        primaryPole = undefined;
      const outA = SunSailLogic.calcDistanceOut(old, PoleIndex.B);
      const outB = SunSailLogic.calcDistanceOut(old, PoleIndex.D);
      if (!eqc(outA, outB, 2)) {
        //   let newSunSail = old;
        //   const minOut = Math.min(outA, outB);
        //   const poleB = newSunSail.poles[PoleIndex.B];
        //   if (poleB.dist.type === "in")
        //     newSunSail = SunSailReducers.setDistIn(newSunSail, PoleIndex.B, SunSailLogic.calcDistanceInFromOut(minOut, SunSailLogic.calcTotalMaxDistInOut(newSunSail, PoleIndex.B)), 0)
        //   else
        //     newSunSail = SunSailReducers.setDistOut(newSunSail, PoleIndex.B, minOut)
        //   const poleD = newSunSail.poles[PoleIndex.D];
        //   if (poleD.dist.type === "in")
        //     newSunSail = SunSailReducers.setDistIn(newSunSail, PoleIndex.D, SunSailLogic.calcDistanceInFromOut(minOut, SunSailLogic.calcTotalMaxDistInOut(newSunSail, PoleIndex.D)), 0)
        //   else
        //     newSunSail = SunSailReducers.setDistOut(newSunSail, PoleIndex.D, minOut)
        //   return newSunSail;
      }
    }
    return old;
  },
  setPolePosition(old: ISunSailData, poleIndex: number, newPos: IVec3, options?: { direct?: boolean }): ISunSailData {
    if (options?.direct) {
      return SunSailReducers.setPoleData(old, poleIndex, {}, PoleReducers.setPosition, newPos);
    }
    const { sunSailType } = old;
    switch (sunSailType) {
      case SunSailType.SolidayCS:
      case SunSailType.SolidayTwin:
      case SunSailType.SolidayXS: {
        return LogicS.setPolePosition(old, poleIndex, newPos);
      }
      case SunSailType.SolidayC: {
        const newSunSail = SunSailReducers.setPolePositionDefault(old, poleIndex, newPos);
        return newSunSail;
      }
      default:
        return SunSailReducers.setPolePositionDefault(old, poleIndex, newPos);
    }
    // return SunSailReducers.setPolePositionDirect(old, poleIndex, newPos);
  },
  fixPosition(old: ISunSailData, poleIndex: number): ISunSailData {
    return SunSailReducers.setPolePosition(old, poleIndex, Poles.getPole(old, poleIndex).position)
  },
  setLength(old: ISunSailData, poleIndex: number, length: number, options?: { direct?: boolean }): ISunSailData {
    const _opt: ISetPoleDataOptions = options?.direct ? { fixPoleData: true, fixPosition: true, fixStandard: true } : { fixPoleData: true, fixPosition: true, fixStandard: true }
    return SunSailReducers.setPoleData(old, poleIndex, _opt, PoleReducers.setLength, length);
  },
  setLengthB(old: ISunSailData, poleIndex: number, lengthB: number, options?: { direct?: boolean }): ISunSailData {
    const _opt: ISetPoleDataOptions = options?.direct ? { fixPoleData: true, fixPosition: true, fixStandard: true } : { fixPoleData: true, fixPosition: true, fixStandard: true }
    return SunSailReducers.setPoleData(old, poleIndex, _opt, PoleReducers.setLengthB, lengthB);
  },
  setLengthBEnabled(old: ISunSailData, poleIndex: number, flag: boolean, options?: { direct?: boolean }): ISunSailData {
    const _opt: ISetPoleDataOptions = options?.direct ? { fixPoleData: true, fixPosition: true, fixStandard: true } : { fixPoleData: true, fixPosition: true, fixStandard: true }
    return SunSailReducers.setPoleData(old, poleIndex, _opt, PoleReducers.setLengthBEnabled, flag);
  },
  setTopMountY(old: ISunSailData, poleIndex: number, topMountY: Auto<number>): ISunSailData {
    return SunSailReducers.setPoleData(old, poleIndex, { fixPoleData: true, fixPosition: true, fixStandard: true }, PoleReducers.setTopMountY, topMountY);
  },
  setAngle(old: ISunSailData, poleIndex: number, angle: number): ISunSailData {
    return SunSailReducers.setPoleData(old, poleIndex, { fixPoleData: true, fixPosition: true, fixStandard: true }, PoleReducers.setAngle, angle);
  },
  setDist(old: ISunSailData, poleIndex: number, dist: IDistIn | IDistOut, options?: { direct?: boolean }): ISunSailData {
    const _opt: ISetPoleDataOptions = options?.direct ? {} : { fixPoleData: true, fixPosition: true, fixStandard: true }
    return SunSailReducers.setPoleData(old, poleIndex, _opt, PoleReducers.setDist, dist);
  },
  setDistIn(old: ISunSailData, poleIndex: number, distIn: Auto<number>, clothIndex: number): ISunSailData {
    return SunSailReducers.setPoleData(old, poleIndex, { fixPoleData: true, fixPosition: true, fixStandard: true }, PoleReducers.setDistIn, distIn, clothIndex);
  },
  setDistOut(old: ISunSailData, poleIndex: number, distOut: Auto<number>): ISunSailData {
    return SunSailReducers.setPoleData(old, poleIndex, { fixPoleData: true, fixPosition: true }, PoleReducers.setDistOut, distOut);
  },
  setDistInSplit(old: ISunSailData, poleIndex: number, flag: boolean): ISunSailData {
    return SunSailReducers.setPoleData(old, poleIndex, { fixPoleData: true, fixPosition: true }, PoleReducers.setDistInSplit, flag);
  },
  setMountToAxle(old: ISunSailData, poleIndex: number, mountToAxle: number | undefined): ISunSailData {
    return SunSailReducers.setPoleData(old, poleIndex, { fixPoleData: true, fixPosition: true, fixStandard: true }, PoleReducers.setMountToAxle, mountToAxle);
  },

  setMaterial(old: ISunSailData, poleId: PoleId, material: MaterialType): ISunSailData {
    return SunSailReducers.setPoleData(old, poleId, { fixPoleData: true }, (pole, material) => {
      pole = ImmHelper.setProperty(pole, "material", material);
      pole = ImmHelper.setProperty(pole, "angle", SunSailLogic.calcNewInitialPoleAngle(ImmHelper.setProperty(old, "poles", poleId, pole), poleId));
      return pole;
    }, material);
  },
  setMount(old: ISunSailData, poleIndex: number, mount: PoleMountType): ISunSailData {
    return SunSailReducers.setPoleData(old, poleIndex, { fixPoleData: true }, (pole, mount) => {
      pole = ImmHelper.setProperty(pole, "mount", mount);
      pole = ImmHelper.setProperty(pole, "angle", SunSailLogic.calcNewInitialPoleAngle(ImmHelper.setProperty(old, "poles", poleIndex, pole), poleIndex));
      return pole;
    }, mount);
  },

  setPoleArticleSlot<T extends keyof IPoleData["articleSlots"]>(old: ISunSailData, poleIndex: number, name: T, slot: IPoleData["articleSlots"][T], options?: { direct?: boolean }): ISunSailData {
    const _opt: ISetPoleDataOptions = options?.direct ? {} : { fixPoleData: true }
    return SunSailReducers.setPoleData(old, poleIndex, _opt, PoleReducers.setArticleSlot, name, slot);
  },
  setCorner(old: ISunSailData, poleIndex: number | undefined, cornerType: CornerType | undefined): ISunSailData {
    if (poleIndex === undefined || ArticleLogic.isCornerForAll(old.sunSailType, cornerType)) {
      for (let i = 0; i < Poles.getCount(old); i++) {
        old = SunSailReducers.setPoleData(old, i, {}, PoleReducers.setCorner, cornerType)
      }
    } else {
      old = SunSailReducers.setPoleData(old, poleIndex, {}, PoleReducers.setCorner, cornerType)
    }
    return old;
  },
  setPylonType(old: ISunSailData, poleIndex: number, pylonType: PylonType | undefined, options?: { direct?: boolean }): ISunSailData {
    const _opt: ISetPoleDataOptions = options?.direct ? {} : { fixPoleData: true }
    return SunSailReducers.setPoleData(old, poleIndex, _opt, PoleReducers.setPylonType, pylonType);
  },
  setCoilUpHeight(old: ISunSailData, poleIndex: number, val: Auto<number>): ISunSailData {
    return SunSailReducers.setPoleData(old, poleIndex, { fixPoleData: true }, PoleReducers.setCoilUpHeight, val);
  },
  setHeightChangeCoilUpHeight(old: ISunSailData, poleIndex: number, val: Auto<number>): ISunSailData {
    return SunSailReducers.setPoleData(old, poleIndex, { fixPoleData: true }, PoleReducers.setHeightChangeCoilUpHeight, val);
  },
  setDistType(old: ISunSailData, poleIndex: number, flag: "in" | "out"): ISunSailData {
    const pole = old.poles[poleIndex];
    let newVal = pole.dist.val;
    if (newVal !== "auto") {
      if (pole.dist.type && pole.dist.type !== flag) {
        if (pole.dist.type === "out") {
          newVal = SunSailLogic.calcDistanceInFromOut(newVal as number, SunSailLogic.calcTotalMaxDistInOut(old, poleIndex))
        } else {
          newVal = SunSailLogic.calcDistanceOutFromIn(newVal as number, SunSailLogic.calcTotalMaxDistInOut(old, poleIndex))
        }
      }
    }

    return SunSailReducers.setPoleData(old, poleIndex, { fixPoleData: true }, PoleReducers.setDistType, flag, newVal as any);
  },
  setPoleType(old: ISunSailData, poleIndex: number, val: PoleType): ISunSailData {
    return SunSailReducers.setPoleData(old, poleIndex, { fixPoleData: true, fixPosition: true, fixStandard: true }, PoleReducers.setPoleType, val);
  },

  updateStandardPositions,
  updateStandardTemplate(old: ISunSailData, template: IStandardTemplate): ISunSailData {

    return SunSailReducers.updateStandardPositions({
      ...old,
      specific: {
        center: Vec3.ZERO,
        rotationY: 0,
        ...old.specific,
        type: "standard",
        sizeId: template.id,
        shape: template.shape
      }
    })
  },
  setStandardShape(old: ISunSailData, standardShape: StandardTemplateShape): ISunSailData {
    if (old.specific?.shape === standardShape)
      return old;

    const templates = StandardTemplate.getTemplatesForShape(standardShape);
    const template = templates[templates.length - 1];
    return SunSailReducers.updateStandardTemplate(old, template);
  },
  setStandardSize(old: ISunSailData, standardSize: StandardTemplateId): ISunSailData {
    if (old.specific?.sizeId === standardSize)
      return old;

    const template = StandardTemplate.getTemplate(standardSize)!;
    return SunSailReducers.updateStandardTemplate(old, template);
  },
  setStandardRotationY(old: ISunSailData, rotationY: number): ISunSailData {
    const specific = old.specific;
    if (specific?.type !== "standard")
      return old;
    return ImmHelper.setProperty(old, "specific", ImmHelper.setProperty(specific, "rotationY", rotationY))
  },
  setStandardCenter(old: ISunSailData, pos: IVec3): ISunSailData {
    const specific = old.specific;
    if (specific?.type !== "standard")
      return old;
    return ImmHelper.setProperty(old, "specific", ImmHelper.setProperty(specific, "center", pos))
  },
  // setSettings<P extends keyof NonNullable<ISunSailData["settings"]>>(old: ISunSailData, param: P, value: NonNullable<ISunSailData["settings"]>[P]): ISunSailData {
  //   return {
  //     ...old,
  //     settings: {
  //       ...old.settings,
  //       [param]: value
  //     }
  //   }
  // },
  setWaterDirection(old: ISunSailData, index: number, waterDirection: WaterDirection): ISunSailData {
    return ImmHelper.setProperty(old, "waterDirections", ImmHelper.setArrayItem(old.waterDirections || [], index, waterDirection))
  },
  setSailMount(old: ISunSailData, poleIndex: number, sailMount: SailMount | undefined) {
    return SunSailReducers.setPoleData(old, poleIndex, {}, (op) => ImmHelper.setProperty(op, "sailMount", sailMount));
  },
  setAxleColor(old: ISunSailData, axleColor: AxleColor | undefined) {
    return ImmHelper.setProperty(old, "axleColor", axleColor);
  },
  setRopeArticleId(old: ISunSailData, name: keyof ISunSailRopes, articleId: string | undefined) {
    if (!old[name])
      old = ImmHelper.setProperty(old, name, {})

    return ImmHelper.setPropertyNonNullable(old, name, "articleId", articleId)
  },
  setRopeLength(old: ISunSailData, name: keyof ISunSailRopes, length: number | undefined) {
    if (!old[name])
      old = ImmHelper.setProperty(old, name, {})

    return ImmHelper.setPropertyNonNullable(old, name, "length", length)
  }
}